import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Typography,
  Space,
  message,
  Alert,
  Popconfirm,
  Switch
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  LockOutlined
} from '@ant-design/icons';
import { userAPI } from '../utils/api';
import { User, CreateUserRequest, UpdateUserRequest } from '../types';
import { useAuth } from '../contexts/AuthContext';

const { Title } = Typography;
const { Option } = Select;

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form] = Form.useForm();
  const { user: currentUser } = useAuth();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const usersData = await userAPI.getUsers();
      setUsers(usersData);
    } catch (err: any) {
      setError(err.message || '获取用户列表失败');
      console.error('获取用户列表失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const showModal = (user?: User) => {
    setEditingUser(user || null);
    setModalVisible(true);
    if (user) {
      form.setFieldsValue({
        username: user.username,
        fullName: user.fullName,
        description: user.description,
        enabled: user.enabled === 1,
        userType: user.userType
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        enabled: true,
        userType: 'admin'
      });
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const userData = {
        ...values,
        enabled: values.enabled ? 1 : 0
      };

      if (editingUser) {
        // 更新用户
        const updatedUserData: UpdateUserRequest = { ...userData };
        if (!values.password) {
          delete updatedUserData.password; // 如果密码为空，不更新密码
        }
        await userAPI.updateUser(editingUser.id, updatedUserData);
        message.success('用户更新成功');
      } else {
        // 创建用户
        await userAPI.createUser(userData as CreateUserRequest);
        message.success('用户创建成功');
      }

      setModalVisible(false);
      fetchUsers();
    } catch (error: any) {
      console.error('操作失败:', error);
      message.error(error.response?.data?.error || '操作失败');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await userAPI.deleteUser(id);
      message.success('用户删除成功');
      fetchUsers();
    } catch (error: any) {
      console.error('删除失败:', error);
      message.error(error.response?.data?.error || '删除失败');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 150,
    },
    {
      title: '姓名',
      dataIndex: 'fullName',
      key: 'fullName',
      width: 150,
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '用户类型',
      dataIndex: 'userType',
      key: 'userType',
      width: 120,
      render: (userType: string) => (
        <Tag color={userType === 'super_admin' ? 'red' : userType === 'admin' ? 'blue' : 'purple'}>
          {userType === 'super_admin' ? '超级管理员' : userType === 'admin' ? '管理员' : '设备编辑员'}
        </Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 100,
      render: (enabled: number) => (
        <Tag color={enabled === 1 ? 'green' : 'red'}>
          {enabled === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      render: (_: any, record: User) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => showModal(record)}
            size="small"
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个用户吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
            disabled={record.username === currentUser?.username}
          >
            <Button
              type="link"
              icon={<DeleteOutlined />}
              danger
              size="small"
              disabled={record.username === currentUser?.username}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 检查当前用户是否是超级管理员
  const isSuperAdmin = currentUser?.userType === 'super_admin';

  if (!isSuperAdmin) {
    return (
      <div>
        <div className="page-header">
          <Title level={2} style={{ margin: 0 }}>
            👥 用户管理
          </Title>
          <p style={{ margin: '8px 0 0 0', color: '#666' }}>
            管理系统用户账户
          </p>
        </div>
        <Alert
          message="权限不足"
          description="只有超级管理员才能访问用户管理功能"
          type="error"
          showIcon
          style={{ margin: '20px 0' }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="加载失败"
        description={error}
        type="error"
        showIcon
        style={{ margin: '20px 0' }}
      />
    );
  }

  return (
    <div>
      <div className="page-header">
        <Title level={2} style={{ margin: 0 }}>
          👥 用户管理
        </Title>
        <p style={{ margin: '8px 0 0 0', color: '#666' }}>
          管理系统用户账户
        </p>
      </div>

      <Card
        className="content-card"
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchUsers}
              loading={loading}
            >
              刷新
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => showModal()}
            >
              新增用户
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{
            total: users.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 个用户`,
          }}
        />
      </Card>

      <Modal
        title={editingUser ? '编辑用户' : '新增用户'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            enabled: true,
            userType: 'admin'
          }}
        >
          <Form.Item
            name="username"
            label="用户名"
            rules={[
              { required: true, message: '请输入用户名!' },
              { min: 3, message: '用户名至少3个字符!' },
              { max: 50, message: '用户名最多50个字符!' }
            ]}
          >
            <Input prefix={<UserOutlined />} placeholder="请输入用户名" />
          </Form.Item>

          <Form.Item
            name="password"
            label="密码"
            rules={editingUser ? [] : [
              { required: true, message: '请输入密码!' },
              { min: 6, message: '密码至少6个字符!' }
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder={editingUser ? "留空表示不修改密码" : "请输入密码"} 
            />
          </Form.Item>

          <Form.Item
            name="fullName"
            label="姓名"
            rules={[
              { required: true, message: '请输入姓名!' },
              { max: 100, message: '姓名最多100个字符!' }
            ]}
          >
            <Input placeholder="请输入姓名" />
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
            rules={[
              { max: 200, message: '描述最多200个字符!' }
            ]}
          >
            <Input.TextArea rows={3} placeholder="请输入用户描述（可选）" />
          </Form.Item>

          <Form.Item
            name="userType"
            label="用户类型"
            rules={[{ required: true, message: '请选择用户类型!' }]}
          >
            <Select placeholder="请选择用户类型">
              <Option value="admin">管理员</Option>
              <Option value="super_admin">超级管理员</Option>
              <Option value="device_editor">设备编辑员（仅设备列表，无法注册/导入/删除）</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="enabled"
            label="启用状态"
            valuePropName="checked"
          >
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Users;
